import type { CapturedObject, CapturedRoom, CapturedSection, CapturedSurface } from "./roomplan.js";
import { heightOf, isUsableTransform, translationOf, widthAxisOf, widthOf } from "./spatial.js";

/**
 * One scan of a whole home, told apart into its rooms.
 *
 * RoomPlan records a walk through a flat as ONE `CapturedRoom` unless the
 * surveyor scans room by room: every wall of every room, the internal
 * partitions included, in one list, with a single floor polygon around the
 * lot. Read as it arrives that is one "room" whose partitions are all outside
 * walls — the heat loss of a two-bedroom flat computed as a single space with
 * a dozen external walls through the middle of it, and one room on the plan
 * where the drawing plainly shows five.
 *
 * WHAT THE CAPTURE GIVES, AND WHAT IT DOES NOT. The walls (each a centred
 * rectangle), the floor polygon, the furniture with its position, and
 * `sections` — the room types RoomPlan recognised ("bedroom", "kitchen"…) but,
 * as the field app encodes them, WITHOUT their position. So the rooms come
 * from the walls, and each room's type from the furniture inside it (a toilet
 * or a bath makes a bathroom, a cooker or a fridge a kitchen, a bed a
 * bedroom, a sofa or a television a living room) — the same evidence
 * RoomPlan's own classifier looks at, placed where it actually stands.
 *
 * HOW. On a 5 cm grid over the floor polygon, the walls are laid down as
 * barriers (a wall end left hanging short of another wall is carried on to
 * it when the gap is under a door's width, because that gap is a doorway the
 * scan saw no wall in); every area the barriers close off is a room. Rooms
 * then grow across the barriers to meet at the walls' centre lines, so the
 * rooms' floors add up to the whole floor. A pocket under 1 m² — a duct, the
 * space between two walls that do not quite meet — joins the room it shares
 * most boundary with. Each room's floor is traced from its cells.
 *
 * WALLS. Each wall is walked along its length, looking a short way to either
 * side: where the two sides are different rooms it is a partition, listed in
 * BOTH rooms under ONE identifier — which is exactly how RoomPlan's own
 * structure merge presents a shared wall, and so `convertCapture` pairs it as
 * internal with no inference; where one side is outside it is that room's
 * external wall; where both sides are the same room it is a stub inside a
 * room, and loses no heat. A wall that runs past more than one room is cut
 * into pieces (`<wall>~<n>`), each carrying its own length; a wall that is
 * one piece keeps its identifier, geometry and everything already recorded
 * against it. Windows, doors and openings follow the piece they sit in, and
 * each is listed in one room only (a door between two rooms in the first of
 * them), because the conversion deducts an opening from its wall wherever the
 * wall is listed but emits it once per listing.
 *
 * IDENTITY. The rooms are `<room>~<n>`, numbered largest first, from the
 * capture's own geometry — so the same capture always splits the same way,
 * and a room's identity does not move when a later correction or calibration
 * changes a wall. A room already split (its identifier ends `~<n>`) is never
 * split again, and a room the walls do not divide is returned untouched.
 *
 * WHEN IT DOES NOT SPLIT. No floor polygon, too few walls, or walls that close
 * off fewer than two rooms: the capture is read exactly as before.
 */

/** Bumped whenever the split would give a different answer for the same capture. */
export const ROOM_SPLIT_VERSION = 2;

const SPLIT_SUFFIX = /~\d+$/;

/**
 * Rooms that come out of a correction or a calibration (applyPlanEdits,
 * applyCalibration). They were made from `capturedRooms` — already split — so
 * they are never split again: a wall a designer draws through a room on the
 * plan changes that room's walls, not how many rooms the scan holds or what
 * they are called. Only the scan as captured is told apart.
 */
const SETTLED = new WeakSet<CapturedRoom>();

export function settledRooms<T extends readonly CapturedRoom[]>(rooms: T): T {
  for (const room of rooms) SETTLED.add(room);
  return rooms;
}

/** True for a room (or surface) this module made by splitting another. */
export function isSplitRef(ref: string): boolean {
  return SPLIT_SUFFIX.test(ref);
}

/** The room a split room was cut from: `abc~2` → `abc`; anything else unchanged. */
export function splitBaseRef(ref: string): string {
  return ref.replace(SPLIT_SUFFIX, "");
}

interface P {
  readonly x: number;
  readonly y: number;
}

/** Grid pitch, metres. */
const CELL = 0.05;
/** Half the thickness a wall is drawn at as a barrier. */
const WALL_HALF = 0.06;
/** A closed-off area smaller than this is a pocket, not a room. */
const MIN_ROOM_M2 = 1.0;
/** How far to either side of a wall's centre line to look for the room there. */
const SIDE_OFFSET = 0.18;
/** A run of wall shorter than this is noise at a junction, not a piece. */
const MIN_PIECE_M = 0.25;
/** The widest gap between a hanging wall end and another wall that is closed as a doorway. */
const GAP_MAX = 1.3;
/** A wall end this close to another wall (or the floor's edge) is joined to it. */
const JOIN_TOL = 0.12;

const OUT = -2;
const BARRIER = -1;

// --- geometry --------------------------------------------------------------

interface Segment {
  readonly a: P;
  readonly b: P;
  readonly len: number;
  /** Unit direction a → b. */
  readonly axis: P;
}

function segmentOfWall(wall: CapturedSurface): Segment | undefined {
  if (!isUsableTransform(wall.transform)) return undefined;
  const axis3 = widthAxisOf(wall.transform);
  const l = Math.hypot(axis3.x, axis3.z);
  const len = widthOf(wall);
  if (!(l > 1e-6) || !(len > 0)) return undefined;
  const axis = { x: axis3.x / l, y: axis3.z / l };
  const c = translationOf(wall.transform);
  return {
    a: { x: c.x - (axis.x * len) / 2, y: c.z - (axis.y * len) / 2 },
    b: { x: c.x + (axis.x * len) / 2, y: c.z + (axis.y * len) / 2 },
    len,
    axis,
  };
}

function toWorld(t: readonly number[], p: readonly number[]): { x: number; y: number; z: number } {
  const [x = 0, y = 0, z = 0] = p;
  return {
    x: t[0]! * x + t[4]! * y + t[8]! * z + t[12]!,
    y: t[1]! * x + t[5]! * y + t[9]! * z + t[13]!,
    z: t[2]! * x + t[6]! * y + t[10]! * z + t[14]!,
  };
}

/** A world point back into a surface's own frame (the transform is rigid). */
function toLocal(t: readonly number[], w: { x: number; y: number; z: number }): number[] {
  const dx = w.x - t[12]!;
  const dy = w.y - t[13]!;
  const dz = w.z - t[14]!;
  return [
    t[0]! * dx + t[1]! * dy + t[2]! * dz,
    t[4]! * dx + t[5]! * dy + t[6]! * dz,
    t[8]! * dx + t[9]! * dy + t[10]! * dz,
  ];
}

function floorOutline(floor: CapturedSurface): P[] | undefined {
  const corners = floor.polygonCorners;
  if (!Array.isArray(corners) || corners.length < 3 || !isUsableTransform(floor.transform)) return undefined;
  return corners.map((c) => {
    const w = toWorld(floor.transform, c);
    return { x: w.x, y: w.z };
  });
}

/**
 * The plan outline of a floor this module cut out of a larger one — its real
 * shape, where the plan otherwise draws a floor as its bounding rectangle — or
 * undefined for any other floor, which the plan keeps drawing as it always has.
 */
export function splitFloorOutline(floor: CapturedSurface): readonly { readonly x: number; readonly y: number }[] | undefined {
  return isSplitRef(floor.identifier) ? floorOutline(floor) : undefined;
}

function pointSegmentDistance(p: P, s: Segment): number {
  const vx = s.b.x - s.a.x;
  const vy = s.b.y - s.a.y;
  const l2 = vx * vx + vy * vy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - s.a.x) * vx + (p.y - s.a.y) * vy) / l2)) : 0;
  return Math.hypot(p.x - (s.a.x + t * vx), p.y - (s.a.y + t * vy));
}

function segmentBetween(a: P, b: P): Segment {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  return { a, b, len, axis: len > 0 ? { x: (b.x - a.x) / len, y: (b.y - a.y) / len } : { x: 1, y: 0 } };
}

/** Distance along the ray `o + t·d` to segment `s`, or undefined when it misses. */
function rayHit(o: P, d: P, s: Segment): number | undefined {
  const ex = s.b.x - s.a.x;
  const ey = s.b.y - s.a.y;
  const den = d.x * ey - d.y * ex;
  if (Math.abs(den) < 1e-9) return undefined;
  const wx = s.a.x - o.x;
  const wy = s.a.y - o.y;
  const t = (wx * ey - wy * ex) / den;
  const u = (wx * d.y - wy * d.x) / den;
  return t > 1e-6 && u >= -1e-6 && u <= 1 + 1e-6 ? t : undefined;
}

function pointInPolygon(p: P, poly: readonly P[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

function signedArea(poly: readonly P[]): number {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
}

/** Douglas–Peucker on an open chain. */
function simplifyChain(points: readonly P[], epsilon: number): P[] {
  if (points.length <= 2) return [...points];
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const chord = segmentBetween(first, last);
  let worst = -1;
  let index = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const d = pointSegmentDistance(points[i]!, chord);
    if (d > worst) {
      worst = d;
      index = i;
    }
  }
  if (worst <= epsilon) return [first, last];
  const left = simplifyChain(points.slice(0, index + 1), epsilon);
  const right = simplifyChain(points.slice(index), epsilon);
  return [...left.slice(0, -1), ...right];
}

function simplifyRing(ring: readonly P[], epsilon: number): P[] {
  if (ring.length <= 4) return [...ring];
  // Start from the vertex farthest from the first, so the chord splits the ring in two.
  const start = ring[0]!;
  let far = 0;
  let best = -1;
  ring.forEach((p, i) => {
    const d = Math.hypot(p.x - start.x, p.y - start.y);
    if (d > best) {
      best = d;
      far = i;
    }
  });
  const a = simplifyChain(ring.slice(0, far + 1), epsilon);
  const b = simplifyChain([...ring.slice(far), start], epsilon);
  const out = [...a.slice(0, -1), ...b.slice(0, -1)];
  return out.length >= 3 ? out : [...ring];
}

// --- the grid --------------------------------------------------------------

interface Grid {
  readonly x0: number;
  readonly y0: number;
  readonly nx: number;
  readonly ny: number;
  readonly label: Int32Array;
}

function cellOf(g: Grid, p: P): number | undefined {
  const i = Math.floor((p.x - g.x0) / CELL);
  const j = Math.floor((p.y - g.y0) / CELL);
  if (i < 0 || j < 0 || i >= g.nx || j >= g.ny) return undefined;
  return j * g.nx + i;
}

function labelAt(g: Grid, p: P): number {
  const c = cellOf(g, p);
  return c === undefined ? OUT : g.label[c]!;
}

/** The region nearest `p` within `reachM`, or OUT. */
function nearestRegion(g: Grid, p: P, reachM: number): number {
  const steps = Math.ceil(reachM / CELL);
  for (let r = 1; r <= steps; r++) {
    for (let k = 0; k < 8 * r; k++) {
      const angle = (2 * Math.PI * k) / (8 * r);
      const l = labelAt(g, { x: p.x + Math.cos(angle) * r * CELL, y: p.y + Math.sin(angle) * r * CELL });
      if (l >= 0) return l;
    }
  }
  return OUT;
}

// --- rooms from walls ------------------------------------------------------

interface Regions {
  readonly grid: Grid;
  readonly count: number;
  /** Per region: its floor outline in plan (world x, z). */
  readonly outlines: readonly P[][];
}

/**
 * `rank`, when given, numbers the rooms instead of "largest first": lower
 * rank first, then larger. A split along a drawn line ranks by which side of
 * the line a room is on, so the two rooms keep their numbers (and so their
 * identities, names and types) when a later correction changes their sizes.
 */
function findRegions(
  outline: readonly P[],
  segments: readonly Segment[],
  rank?: (centroid: P) => number,
): Regions | undefined {
  // Doorways the scan saw no wall in: a wall end hanging free, carried on to
  // whatever it points at within a door's width, or else joined to the
  // nearest other free end.
  const outlineSegs = outline.map((p, i) => segmentBetween(p, outline[(i + 1) % outline.length]!));
  const barriers: Segment[] = [...segments];
  const free: { p: P; d: P; owner: number }[] = [];
  segments.forEach((s, index) => {
    for (const [p, d] of [
      [s.a, { x: -s.axis.x, y: -s.axis.y }],
      [s.b, s.axis],
    ] as const) {
      const attached =
        segments.some((o, j) => j !== index && pointSegmentDistance(p, o) <= JOIN_TOL) ||
        outlineSegs.some((o) => pointSegmentDistance(p, o) <= JOIN_TOL);
      if (!attached) free.push({ p, d, owner: index });
    }
  });
  const joined = new Set<number>();
  free.forEach((end, index) => {
    let best: number | undefined;
    for (const [j, s] of [...segments.entries(), ...outlineSegs.map((o, k) => [-1 - k, o] as const)]) {
      if (j === end.owner) continue;
      const t = rayHit(end.p, end.d, s);
      if (t !== undefined && t <= GAP_MAX && (best === undefined || t < best)) best = t;
    }
    if (best !== undefined) {
      const reach = best + WALL_HALF;
      barriers.push(segmentBetween(end.p, { x: end.p.x + end.d.x * reach, y: end.p.y + end.d.y * reach }));
      joined.add(index);
    }
  });
  const paired = new Set<string>();
  free.forEach((end, index) => {
    if (joined.has(index)) return;
    let partner: number | undefined;
    let bestD = GAP_MAX;
    free.forEach((other, j) => {
      if (j === index || other.owner === end.owner) return;
      const d = Math.hypot(other.p.x - end.p.x, other.p.y - end.p.y);
      if (d < bestD) {
        bestD = d;
        partner = j;
      }
    });
    if (partner === undefined) return;
    const pair = [index, partner].sort((a, b) => a - b).join(":");
    if (paired.has(pair)) return;
    paired.add(pair);
    barriers.push(segmentBetween(end.p, free[partner]!.p));
  });

  const xs = [...outline.map((p) => p.x), ...segments.flatMap((s) => [s.a.x, s.b.x])];
  const ys = [...outline.map((p) => p.y), ...segments.flatMap((s) => [s.a.y, s.b.y])];
  const pad = 0.3;
  const x0 = Math.min(...xs) - pad;
  const y0 = Math.min(...ys) - pad;
  const nx = Math.ceil((Math.max(...xs) + pad - x0) / CELL);
  const ny = Math.ceil((Math.max(...ys) + pad - y0) / CELL);
  if (!(nx > 0 && ny > 0) || nx * ny > 4_000_000) return undefined;
  const label = new Int32Array(nx * ny).fill(OUT);
  const grid: Grid = { x0, y0, nx, ny, label };
  const centre = (c: number): P => ({ x: x0 + ((c % nx) + 0.5) * CELL, y: y0 + (Math.floor(c / nx) + 0.5) * CELL });

  for (let c = 0; c < label.length; c++) if (pointInPolygon(centre(c), outline)) label[c] = 0;
  const inside = Uint8Array.from(label, (l) => (l === 0 ? 1 : 0));
  for (const s of barriers) {
    const reach = WALL_HALF + CELL;
    const i0 = Math.max(0, Math.floor((Math.min(s.a.x, s.b.x) - reach - x0) / CELL));
    const i1 = Math.min(nx - 1, Math.floor((Math.max(s.a.x, s.b.x) + reach - x0) / CELL));
    const j0 = Math.max(0, Math.floor((Math.min(s.a.y, s.b.y) - reach - y0) / CELL));
    const j1 = Math.min(ny - 1, Math.floor((Math.max(s.a.y, s.b.y) + reach - y0) / CELL));
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const c = j * nx + i;
        if (inside[c] && pointSegmentDistance(centre(c), s) <= WALL_HALF) label[c] = BARRIER;
      }
    }
  }

  // Every open area the barriers close off, 4-connected. Unvisited open cells
  // are marked -3 first, so a region's number can never be mistaken for one.
  const UNVISITED = -3;
  for (let c = 0; c < label.length; c++) if (label[c] === 0) label[c] = UNVISITED;
  let count = 0;
  const queue = new Int32Array(label.length);
  const neighbours = (c: number): number[] => {
    const i = c % nx;
    const out: number[] = [];
    if (i > 0) out.push(c - 1);
    if (i < nx - 1) out.push(c + 1);
    if (c >= nx) out.push(c - nx);
    if (c < label.length - nx) out.push(c + nx);
    return out;
  };
  for (let c = 0; c < label.length; c++) {
    if (label[c] !== UNVISITED) continue;
    let head = 0;
    let tail = 0;
    queue[tail++] = c;
    label[c] = count;
    while (head < tail) {
      const at = queue[head++]!;
      for (const n of neighbours(at)) {
        if (label[n] === UNVISITED) {
          label[n] = count;
          queue[tail++] = n;
        }
      }
    }
    count++;
  }
  if (count < 2) return undefined;

  // Rooms grow across the walls to meet at their centre lines.
  {
    let head = 0;
    let tail = 0;
    for (let c = 0; c < label.length; c++) if (label[c]! >= 0) queue[tail++] = c;
    while (head < tail) {
      const at = queue[head++]!;
      for (const n of neighbours(at)) {
        if (label[n] === BARRIER) {
          label[n] = label[at]!;
          queue[tail++] = n;
        }
      }
    }
  }

  // Pockets join the room they share most boundary with, smallest first.
  const areaOf = (): number[] => {
    const a = new Array<number>(count).fill(0);
    for (const l of label) if (l >= 0) a[l]!++;
    return a;
  };
  for (;;) {
    const cells = areaOf();
    const small = cells
      .map((n, r) => ({ r, m2: n * CELL * CELL }))
      .filter((x) => x.m2 > 0 && x.m2 < MIN_ROOM_M2)
      .sort((p, q) => p.m2 - q.m2)[0];
    if (!small) break;
    const shared = new Map<number, number>();
    for (let c = 0; c < label.length; c++) {
      if (label[c] !== small.r) continue;
      for (const n of neighbours(c)) {
        const l = label[n]!;
        if (l >= 0 && l !== small.r) shared.set(l, (shared.get(l) ?? 0) + 1);
      }
    }
    const into = [...shared.entries()].sort((p, q) => q[1] - p[1] || p[0] - q[0])[0]?.[0];
    for (let c = 0; c < label.length; c++) if (label[c] === small.r) label[c] = into ?? OUT;
  }

  // Renumber: largest first, ties by position, so the same capture always splits the same way.
  const cells = areaOf();
  const sums = cells.map(() => ({ x: 0, y: 0 }));
  for (let c = 0; c < label.length; c++) {
    const l = label[c]!;
    if (l < 0) continue;
    const p = centre(c);
    sums[l]!.x += p.x;
    sums[l]!.y += p.y;
  }
  const order = cells
    .map((n, r) => ({ r, n, cx: n > 0 ? sums[r]!.x / n : 0, cy: n > 0 ? sums[r]!.y / n : 0 }))
    .filter((x) => x.n > 0)
    .map((x) => ({ ...x, rank: rank ? rank({ x: x.cx, y: x.cy }) : 0 }))
    .sort((p, q) => p.rank - q.rank || q.n - p.n || p.cx - q.cx || p.cy - q.cy);
  if (order.length < 2) return undefined;
  const renumber = new Map(order.map((x, k) => [x.r, k]));
  for (let c = 0; c < label.length; c++) {
    const l = label[c]!;
    if (l >= 0) label[c] = renumber.get(l) ?? OUT;
  }

  const outlines = order.map((_, k) => traceOutline(grid, k));
  if (outlines.some((o) => o.length < 3)) return undefined;
  return { grid, count: order.length, outlines };
}

/** The outer boundary of region `k`, as a simplified polygon in plan. */
function traceOutline(g: Grid, k: number): P[] {
  const { nx, ny, label } = g;
  const at = (i: number, j: number): number => (i < 0 || j < 0 || i >= nx || j >= ny ? OUT : label[j * nx + i]!);
  // Directed cell-side edges with the region on the left, keyed by start corner.
  const next = new Map<number, number[]>();
  const W = nx + 1;
  const key = (i: number, j: number): number => j * W + i;
  const add = (i0: number, j0: number, i1: number, j1: number): void => {
    const list = next.get(key(i0, j0)) ?? [];
    list.push(key(i1, j1));
    next.set(key(i0, j0), list);
  };
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      if (at(i, j) !== k) continue;
      if (at(i, j - 1) !== k) add(i, j, i + 1, j);
      if (at(i + 1, j) !== k) add(i + 1, j, i + 1, j + 1);
      if (at(i, j + 1) !== k) add(i + 1, j + 1, i, j + 1);
      if (at(i - 1, j) !== k) add(i, j + 1, i, j);
    }
  }
  const loops: P[][] = [];
  for (const start of [...next.keys()]) {
    while ((next.get(start)?.length ?? 0) > 0) {
      const loop: number[] = [start];
      let cur = next.get(start)!.pop()!;
      while (cur !== start) {
        loop.push(cur);
        const outs = next.get(cur);
        if (!outs || outs.length === 0) break;
        cur = outs.pop()!;
      }
      loops.push(loop.map((v) => ({ x: g.x0 + (v % W) * CELL, y: g.y0 + Math.floor(v / W) * CELL })));
    }
  }
  const outer = loops.sort((a, b) => Math.abs(signedArea(b)) - Math.abs(signedArea(a)))[0] ?? [];
  // Drop the staircase's collinear corners, then simplify to the walls' own lines.
  const trimmed = outer.filter((p, i) => {
    const a = outer[(i - 1 + outer.length) % outer.length]!;
    const b = outer[(i + 1) % outer.length]!;
    return Math.abs((p.x - a.x) * (b.y - a.y) - (p.y - a.y) * (b.x - a.x)) > 1e-12;
  });
  return simplifyRing(trimmed, CELL * 1.5);
}

// --- furniture tells the room ----------------------------------------------

const FURNITURE_VOTES: Readonly<Record<string, readonly (readonly [string, number])[]>> = {
  toilet: [["bathroom", 5]],
  bathtub: [["bathroom", 5]],
  sink: [
    ["bathroom", 0.6],
    ["kitchen", 0.5],
  ],
  bed: [["bedroom", 5]],
  stove: [["kitchen", 5]],
  oven: [["kitchen", 5]],
  refrigerator: [["kitchen", 4]],
  dishwasher: [["kitchen", 4]],
  washerDryer: [["utility", 1.5]],
  sofa: [["livingRoom", 3]],
  television: [["livingRoom", 2]],
  fireplace: [["livingRoom", 2]],
  table: [["diningRoom", 1]],
  chair: [["diningRoom", 0.5]],
  stairs: [["hallway", 3]],
};

function inferSection(objects: readonly CapturedObject[], story: number | undefined): CapturedSection | undefined {
  const score = new Map<string, number>();
  for (const o of objects) {
    for (const [label, weight] of FURNITURE_VOTES[o.category ?? ""] ?? []) {
      score.set(label, (score.get(label) ?? 0) + weight);
    }
  }
  const best = [...score.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
  if (!best || best[1] < 1.5) return undefined;
  return { label: best[0], ...(story !== undefined ? { story } : {}) };
}

// --- the split --------------------------------------------------------------

interface Piece {
  readonly id: string;
  readonly surface: CapturedSurface;
  /** Along the wall, metres from its `a` end. */
  readonly from: number;
  readonly to: number;
  readonly rooms: readonly number[];
}

function withTranslation(t: readonly number[], x: number, z: number): number[] {
  const out = [...t];
  out[12] = x;
  out[14] = z;
  return out;
}

/** Where an opening sits along its wall: metres from the wall's `a` end. */
interface Span {
  readonly from: number;
  readonly to: number;
  readonly centre: number;
}

function piecesOfWall(wall: CapturedSurface, seg: Segment, g: Grid, openings: readonly Span[]): Piece[] {
  const normal = { x: -seg.axis.y, y: seg.axis.x };
  const samples = Math.max(1, Math.round(seg.len / CELL));
  const step = seg.len / samples;
  const pairs: { at: number; left: number; right: number }[] = [];
  for (let s = 0; s < samples; s++) {
    const d = (s + 0.5) * step;
    const p = { x: seg.a.x + seg.axis.x * d, y: seg.a.y + seg.axis.y * d };
    const left = labelAt(g, { x: p.x + normal.x * SIDE_OFFSET, y: p.y + normal.y * SIDE_OFFSET });
    const right = labelAt(g, { x: p.x - normal.x * SIDE_OFFSET, y: p.y - normal.y * SIDE_OFFSET });
    pairs.push({ at: d, left: left < 0 ? OUT : left, right: right < 0 ? OUT : right });
  }

  // Runs of the same pair, with slivers at junctions folded into their neighbour.
  interface Run {
    from: number;
    to: number;
    left: number;
    right: number;
  }
  let runs: Run[] = [];
  for (const p of pairs) {
    const last = runs[runs.length - 1];
    if (last && last.left === p.left && last.right === p.right) last.to = p.at + step / 2;
    else runs.push({ from: p.at - step / 2, to: p.at + step / 2, left: p.left, right: p.right });
  }
  for (let changed = true; changed && runs.length > 1; ) {
    changed = false;
    const shortest = runs
      .map((r, i) => ({ i, len: r.to - r.from }))
      .filter((x) => x.len < MIN_PIECE_M)
      .sort((a, b) => a.len - b.len)[0];
    if (!shortest) break;
    const i = shortest.i;
    const r = runs[i]!;
    const neighbour = i > 0 ? runs[i - 1]! : runs[i + 1]!;
    if (i > 0) neighbour.to = r.to;
    else neighbour.from = r.from;
    runs.splice(i, 1);
    // Neighbours that now carry the same pair become one run.
    const merged: Run[] = [];
    for (const x of runs) {
      const last = merged[merged.length - 1];
      if (last && last.left === x.left && last.right === x.right) last.to = x.to;
      else merged.push({ ...x });
    }
    runs = merged;
    changed = true;
  }

  // A wall is never cut inside a window, door or opening, or one piece would
  // carry more opening than wall (and the capture be refused as
  // unconvertible). A partition cannot really meet an outside wall in the
  // middle of a window, so the cut is noise: it moves to the opening's near
  // edge, and the piece holding the opening's centre takes all of it. Only
  // when the opening runs past the neighbouring piece altogether are the two
  // pieces made one, under the pair that holds the opening.
  for (let moved = true; moved && runs.length > 1; ) {
    moved = false;
    for (let i = 0; i < runs.length - 1; i++) {
      const at = runs[i]!.to;
      const span = openings.find((o) => at > o.from + 0.01 && at < o.to - 0.01);
      if (!span) continue;
      const cutTo = span.centre > at ? span.from : span.to;
      if (cutTo > runs[i]!.from + MIN_PIECE_M && cutTo < runs[i + 1]!.to - MIN_PIECE_M) {
        runs[i]!.to = cutTo;
        runs[i + 1]!.from = cutTo;
      } else {
        const keep = span.centre <= at ? runs[i]! : runs[i + 1]!;
        runs.splice(i, 2, { from: runs[i]!.from, to: runs[i + 1]!.to, left: keep.left, right: keep.right });
        const merged: Run[] = [];
        for (const x of runs) {
          const last = merged[merged.length - 1];
          if (last && last.left === x.left && last.right === x.right) last.to = x.to;
          else merged.push({ ...x });
        }
        runs = merged;
      }
      moved = true;
      break;
    }
  }

  const roomsOf = (r: Run): number[] => {
    if (r.left >= 0 && r.right >= 0) return r.left === r.right ? [] : [r.left, r.right];
    if (r.left >= 0) return [r.left];
    if (r.right >= 0) return [r.right];
    // Neither side reads as a room (a wall drawn just outside the floor): it
    // belongs to the room nearest its middle, as an outside wall.
    const mid = (r.from + r.to) / 2;
    const near = nearestRegion(g, { x: seg.a.x + seg.axis.x * mid, y: seg.a.y + seg.axis.y * mid }, 0.6);
    return near >= 0 ? [near] : [];
  };

  if (runs.length === 1) {
    return [{ id: wall.identifier, surface: wall, from: 0, to: seg.len, rooms: roomsOf(runs[0]!) }];
  }
  const { polygonCorners: _notARectangleAnyMore, ...rectangle } = wall;
  return runs.map((r, n) => {
    const from = Math.max(0, r.from);
    const to = Math.min(seg.len, r.to);
    const mid = (from + to) / 2;
    const id = `${wall.identifier}~${n}`;
    // Only the plan position moves: the wall's height and its centre's height stay.
    const surface: CapturedSurface = {
      ...rectangle,
      identifier: id,
      dimensions: [to - from, heightOf(wall), wall.dimensions?.[2] ?? 0],
      transform: withTranslation(wall.transform, seg.a.x + seg.axis.x * mid, seg.a.y + seg.axis.y * mid),
    };
    return { id, surface, from, to, rooms: roomsOf(r) };
  });
}

/**
 * The rooms one captured room divides into, or `[room]` when it does not.
 * `baseRef` is the room's own identity (`capturedRoomRef`).
 */
export function splitCapturedRoom(room: CapturedRoom, baseRef: string): readonly CapturedRoom[] {
  if (SETTLED.has(room)) return [room];
  if (typeof room.identifier === "string" && isSplitRef(room.identifier)) return [room];
  const walls = room.walls ?? [];
  const floor = (room.floors ?? [])[0];
  if (!floor || walls.length < 4) return [room];
  const outline = floorOutline(floor);
  if (!outline) return [room];
  return divideRoom(room, baseRef, floor, outline, walls) ?? [room];
}

/** The rooms `walls` close off inside `outline`, built from `room`; undefined when fewer than two. */
function divideRoom(
  room: CapturedRoom,
  baseRef: string,
  floor: CapturedSurface,
  outline: readonly P[],
  walls: readonly CapturedSurface[],
  rank?: (centroid: P) => number,
): readonly CapturedRoom[] | undefined {
  const segs = walls.map((w) => segmentOfWall(w));
  const usable = segs.filter((s): s is Segment => s !== undefined);
  if (usable.length < 3) return undefined;
  const regions = findRegions(outline, usable, rank);
  if (!regions) return undefined;
  const { grid, count, outlines } = regions;

  // Walls, cut where the room on either side changes.
  const pieces: Piece[] = [];
  const piecesByWall = new Map<string, { seg: Segment; pieces: Piece[] }>();
  walls.forEach((wall, i) => {
    const seg = segs[i];
    if (!seg) {
      // No plan position: kept whole, in the room nearest nothing — the largest.
      const piece = { id: wall.identifier, surface: wall, from: 0, to: 0, rooms: [0] };
      pieces.push(piece);
      piecesByWall.set(wall.identifier, { seg: segmentBetween({ x: 0, y: 0 }, { x: 0, y: 0 }), pieces: [piece] });
      return;
    }
    const spans: Span[] = [...(room.doors ?? []), ...(room.windows ?? []), ...(room.openings ?? [])]
      .filter((o) => o.parentIdentifier === wall.identifier && isUsableTransform(o.transform))
      .map((o) => {
        const c = translationOf(o.transform);
        const along = (c.x - seg.a.x) * seg.axis.x + (c.z - seg.a.y) * seg.axis.y;
        const half = widthOf(o) / 2;
        return { from: along - half, to: along + half, centre: along };
      });
    const own = piecesOfWall(wall, seg, grid, spans);
    pieces.push(...own);
    piecesByWall.set(wall.identifier, { seg, pieces: own });
  });

  const out = Array.from({ length: count }, () => ({
    walls: [] as CapturedSurface[],
    doors: [] as CapturedSurface[],
    windows: [] as CapturedSurface[],
    openings: [] as CapturedSurface[],
    objects: [] as CapturedObject[],
  }));
  for (const piece of pieces) for (const r of piece.rooms) out[r]!.walls.push(piece.surface);

  // Openings follow the piece they sit in; each is listed in one room.
  const place = (surface: CapturedSurface, into: "doors" | "windows" | "openings"): void => {
    const host = surface.parentIdentifier ? piecesByWall.get(surface.parentIdentifier) : undefined;
    const centre = isUsableTransform(surface.transform) ? translationOf(surface.transform) : undefined;
    if (host && centre) {
      const along = (centre.x - host.seg.a.x) * host.seg.axis.x + (centre.z - host.seg.a.y) * host.seg.axis.y;
      const piece =
        host.pieces.find((p) => along >= p.from && along <= p.to) ??
        [...host.pieces].sort(
          (a, b) =>
            Math.min(Math.abs(along - a.from), Math.abs(along - a.to)) -
            Math.min(Math.abs(along - b.from), Math.abs(along - b.to)),
        )[0];
      if (piece && piece.rooms.length > 0) {
        const room = Math.min(...piece.rooms);
        out[room]![into].push(piece.id === surface.parentIdentifier ? surface : { ...surface, parentIdentifier: piece.id });
        return;
      }
    }
    const r = centre ? nearestRegion(grid, { x: centre.x, y: centre.z }, 1.0) : OUT;
    out[r >= 0 ? r : 0]![into].push(surface);
  };
  for (const s of room.doors ?? []) place(s, "doors");
  for (const s of room.windows ?? []) place(s, "windows");
  for (const s of room.openings ?? []) place(s, "openings");

  for (const o of room.objects ?? []) {
    const t = o.transform;
    const at = t && isUsableTransform(t) ? translationOf(t) : undefined;
    const label = at ? labelAt(grid, { x: at.x, y: at.z }) : OUT;
    const r = label >= 0 ? label : at ? nearestRegion(grid, { x: at.x, y: at.z }, 1.0) : OUT;
    out[r >= 0 ? r : 0]!.objects.push(o);
  }

  const floorY = translationOf(floor.transform).y;
  return out.map((parts, k) => {
    const poly = outlines[k]!;
    const corners = poly.map((p) => toLocal(floor.transform, { x: p.x, y: floorY, z: p.y }));
    const lx = corners.map((c) => c[0]!);
    const ly = corners.map((c) => c[1]!);
    const splitFloor: CapturedSurface = {
      ...floor,
      identifier: `${floor.identifier}~${k}`,
      polygonCorners: corners,
      dimensions: [Math.max(...lx) - Math.min(...lx), Math.max(...ly) - Math.min(...ly), 0],
    };
    const section = inferSection(parts.objects, room.story);
    return {
      identifier: `${baseRef}~${k}`,
      ...(room.story !== undefined ? { story: room.story } : {}),
      ...(room.version !== undefined ? { version: room.version } : {}),
      walls: parts.walls,
      doors: parts.doors,
      windows: parts.windows,
      openings: parts.openings,
      floors: [splitFloor],
      objects: parts.objects,
      sections: section ? [section] : [],
    };
  });
}

// --- a designer's split and merge (plan corrections) ------------------------

/** A line drawn across a room on the plan, in plan metres (x = world X, y = world Z). */
export interface RoomCutLine {
  readonly start: { readonly x: number; readonly y: number };
  readonly end: { readonly x: number; readonly y: number };
}

/** A floor's outline: its polygon when it has one, else the rectangle its dimensions give. */
function floorShape(floor: CapturedSurface): P[] | undefined {
  const polygon = floorOutline(floor);
  if (polygon) return polygon;
  if (!isUsableTransform(floor.transform)) return undefined;
  const t = floor.transform;
  const al = Math.hypot(t[0]!, t[2]!);
  if (!(al > 1e-6)) return undefined;
  const along = { x: t[0]! / al, y: t[2]! / al };
  const cl = Math.hypot(t[4]!, t[6]!);
  const across = cl > 1e-6 ? { x: t[4]! / cl, y: t[6]! / cl } : { x: -along.y, y: along.x };
  const w = widthOf(floor);
  const d = heightOf(floor);
  if (!(w > 0) || !(d > 0)) return undefined;
  const c = { x: t[12]!, y: t[14]! };
  const at = (sw: number, sd: number): P => ({
    x: c.x + (along.x * w * sw) / 2 + (across.x * d * sd) / 2,
    y: c.y + (along.y * w * sw) / 2 + (across.y * d * sd) / 2,
  });
  return [at(-1, -1), at(1, -1), at(1, 1), at(-1, 1)];
}

/**
 * One room divided in two along a line a designer drew across it — the plan's
 * "Split room". The line is carried on to the walls it was drawn between (a
 * click a little short of a wall still reaches it) and becomes a partition
 * listed in both rooms under one identifier (`<room>~cut`), so the heat loss
 * reads it as internal. The rooms are `<room>~0` (left of the line, from its
 * start to its end) and `<room>~1` (right), whatever their sizes, so a later
 * correction never swaps them. Undefined when the line does not divide the
 * room in two — it misses the room, or ends in the open.
 */
export function splitRoomAlong(room: CapturedRoom, ref: string, line: RoomCutLine): readonly CapturedRoom[] | undefined {
  const floors = room.floors ?? [];
  if (floors.length !== 1) return undefined;
  const floor = floors[0]!;
  const outline = floorShape(floor);
  if (!outline) return undefined;
  const walls = room.walls ?? [];
  const len = Math.hypot(line.end.x - line.start.x, line.end.y - line.start.y);
  if (!(len > 0.2)) return undefined;
  const d = { x: (line.end.x - line.start.x) / len, y: (line.end.y - line.start.y) / len };

  // Carry each end on to the wall (or the floor's edge) it was drawn towards.
  const stops = [
    ...walls.map((w) => segmentOfWall(w)).filter((s): s is Segment => s !== undefined),
    ...outline.map((p, i) => segmentBetween(p, outline[(i + 1) % outline.length]!)),
  ];
  const reach = (from: P, dir: P): P => {
    let best: number | undefined;
    for (const s of stops) {
      const t = rayHit(from, dir, s);
      if (t !== undefined && t <= 1.0 && (best === undefined || t < best)) best = t;
    }
    const go = (best ?? 0) + WALL_HALF * 2;
    return { x: from.x + dir.x * go, y: from.y + dir.y * go };
  };
  const a = reach(line.start, { x: -d.x, y: -d.y });
  const b = reach(line.end, d);
  const cutLen = Math.hypot(b.x - a.x, b.y - a.y);

  const heights = walls.map((w) => heightOf(w)).filter((h) => h > 0).sort((p, q) => p - q);
  const height = heights.length > 0 ? heights[Math.floor(heights.length / 2)]! : 2.4;
  const bases = walls
    .filter((w) => isUsableTransform(w.transform))
    .map((w) => translationOf(w.transform).y - heightOf(w) / 2)
    .sort((p, q) => p - q);
  const base = bases.length > 0 ? bases[Math.floor(bases.length / 2)]! : translationOf(floor.transform).y;
  // Local X along the cut, Y up, Z = X × Y: a vertical wall, as RoomPlan draws one.
  const partition: CapturedSurface = {
    identifier: `${ref}~cut`,
    dimensions: [cutLen, height, 0.1],
    transform: [d.x, 0, d.y, 0, 0, 1, 0, 0, -d.y, 0, d.x, 0, (a.x + b.x) / 2, base + height / 2, (a.y + b.y) / 2, 1],
    confidence: "high",
    ...(room.story !== undefined ? { story: room.story } : {}),
    category: "wall",
  };

  const side = (c: P): number => (d.x * (c.y - line.start.y) - d.y * (c.x - line.start.x) > 0 ? 0 : 1);
  const rooms = divideRoom(room, ref, floor, outline, [...walls, partition], side);
  return rooms && rooms.length === 2 ? rooms : undefined;
}

/**
 * Two rooms made one — the plan's "Merge rooms": an open-plan kitchen and
 * living room the scan (or the split) told apart, or a hall run into a
 * lounge. What stood between them goes: a wall listed in both, and a pair of
 * walls one in each that lie on one another (two captures' faces of one
 * partition), with the doors and openings in them. Everything else of both is
 * kept — both floors (the room's area is theirs together), every other wall
 * and opening, the furniture — under the identity of `into`.
 */
export function mergeRoomsInto(into: CapturedRoom, other: CapturedRoom, intoRef: string): CapturedRoom {
  const aWalls = into.walls ?? [];
  const bWalls = other.walls ?? [];
  const shared = new Set(aWalls.map((w) => w.identifier).filter((id) => bWalls.some((w) => w.identifier === id)));
  const lies = (x: CapturedSurface, y: CapturedSurface): boolean => {
    const s = segmentOfWall(x);
    const t = segmentOfWall(y);
    if (!s || !t) return false;
    if (Math.abs(s.axis.x * t.axis.y - s.axis.y * t.axis.x) > 0.17) return false;
    if (pointSegmentDistance(t.a, { ...s, a: { x: s.a.x - s.axis.x * 100, y: s.a.y - s.axis.y * 100 }, b: { x: s.b.x + s.axis.x * 100, y: s.b.y + s.axis.y * 100 } }) > 0.25) return false;
    const along = (p: P) => (p.x - s.a.x) * s.axis.x + (p.y - s.a.y) * s.axis.y;
    const lo = Math.max(0, Math.min(along(t.a), along(t.b)));
    const hi = Math.min(s.len, Math.max(along(t.a), along(t.b)));
    return hi - lo >= 0.5 * Math.min(s.len, t.len);
  };
  const gone = new Set(shared);
  for (const x of aWalls) for (const y of bWalls) if (!shared.has(x.identifier) && lies(x, y)) {
    gone.add(x.identifier);
    gone.add(y.identifier);
  }
  const keep = (list: readonly CapturedSurface[] | undefined) =>
    (list ?? []).filter((o) => !o.parentIdentifier || !gone.has(o.parentIdentifier));
  const walls = [...aWalls, ...bWalls.filter((w) => !shared.has(w.identifier))].filter((w) => !gone.has(w.identifier));
  return {
    ...into,
    identifier: intoRef,
    walls,
    doors: [...keep(into.doors), ...keep(other.doors)],
    windows: [...keep(into.windows), ...keep(other.windows)],
    openings: [...keep(into.openings), ...keep(other.openings)],
    floors: [...(into.floors ?? []), ...(other.floors ?? [])],
    objects: [...(into.objects ?? []), ...(other.objects ?? [])],
    sections: (into.sections ?? []).length > 0 ? into.sections! : (other.sections ?? []),
  };
}

const SPLIT_CACHE = new WeakMap<object, readonly CapturedRoom[]>();

/**
 * Every captured room, each divided into the rooms its walls close off. The
 * same array comes back for the same input when nothing divides, so callers
 * that compare by identity see no change; a divided capture is computed once.
 */
export function splitCapturedRooms(
  rooms: readonly CapturedRoom[],
  refOf: (room: CapturedRoom, index: number) => string,
): readonly CapturedRoom[] {
  const cached = SPLIT_CACHE.get(rooms);
  if (cached) return cached;
  let changed = false;
  const out: CapturedRoom[] = [];
  rooms.forEach((room, index) => {
    const parts = splitCapturedRoom(room, refOf(room, index));
    if (parts.length !== 1 || parts[0] !== room) changed = true;
    out.push(...parts);
  });
  const result = changed ? out : rooms;
  SPLIT_CACHE.set(rooms, result);
  return result;
}
